import type { Reader } from '@stripe/terminal-js'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { useCore } from '@/composables/core'
import { useStorage } from '@/composables/storage'
import { useAnalytics } from '@/composables/analytics'
import { useDevLogger } from '@/composables/dev-logger'

import { ENDPOINTS } from '@/lib/consts.ts'
import {
  INTEGRATION_MODES,
  READER_STATUSES,
  EVENTS_SCREENS,
  EVENTS_ACTION_NAME,
} from '@/components/settings/payments/payment-types/booksy-card-reader/consts'

const logger = useDevLogger('stripe-sdi')

async function processPayment (readerId: string, paymentIntentId: string): Promise<unknown> {
  const { apiCall, store } = useCore()
  const businessId = store.getters['business/businessId']

  return apiCall({
    method: 'POST',
    url: ENDPOINTS.STRIPE_SDI_PROCESS_PAYMENT,
    urlParams: {
      businessId,
      readerId,
      paymentIntentId,
    },
  })
}

async function cancelPayment (readerId: string): Promise<unknown> {
  const { apiCall, store } = useCore()
  const businessId = store.getters['business/businessId']

  return apiCall({
    method: 'POST',
    url: ENDPOINTS.STRIPE_SDI_CANCEL_PAYMENT,
    urlParams: {
      businessId,
      readerId,
    },
  })
}

function checkIfReaderIsOnline (readers: Reader[], reader: Reader): void {
  const SDIReader = readers.find(SDIReader => SDIReader.serial_number === reader.serial_number)

  if (!SDIReader) {
    throw new Error('SDI Reader not found in registeredReaders')
  }

  if (SDIReader.status !== READER_STATUSES.ONLINE) {
    throw new Error(`Failed to choose reader. Status: ${SDIReader.status}`)
  }
}

async function switchIntegrationModeToSDI (): Promise<void> {
  logger.debug(`switching integration mode...`)

  const { apiCall, store } = useCore()
  const businessId = store.getters['business/businessId']

  await apiCall({
    method: 'POST',
    url: ENDPOINTS.STRIPE_SDI_MODE_CHANGE,
    urlParams: {
      businessId,
    },
  })

  await store.dispatch('business/loadStripeAccount')

  logger.debug(`integration mode: ${(store.state as { business: { stripeAccount: { integration_mode?: string } } }).business.stripeAccount.integration_mode}`)
}

// Only for analytics purpose
// we want to measure how long reader is busy with same payment_intent
function checkReaderBusyTime (reader: Reader): void {
  const storage = useStorage()
  const analytics = useAnalytics()
  const { EVENTS } = analytics

  const { serial_number } = reader
  const payment_intent = reader.action?.process_payment_intent?.payment_intent
  const date = Date.now()

  const savedReaders = storage.getItem((storage.KEYS as { BCR_BUSY_READERS: string }).BCR_BUSY_READERS) as Record<string, { payment_intent?: string; date: number; reported: boolean }> | null
  if (!savedReaders || !Object.keys(savedReaders).length) {
    const readersToSave = {
      [String(serial_number)]: {
        payment_intent,
        date,
        reported: false,
      },
    }
    storage.setItem((storage.KEYS as { BCR_BUSY_READERS: string }).BCR_BUSY_READERS, readersToSave)
    return
  }

  const savedReader = savedReaders[String(serial_number)]
  const differentPaymentIntent = savedReader?.payment_intent !== payment_intent
  if (!savedReader || differentPaymentIntent) {
    const readersToSave = {
      ...savedReader,
      [String(serial_number)]: {
        payment_intent,
        date,
        reported: false,
      },
    }
    storage.setItem((storage.KEYS as { BCR_BUSY_READERS: string }).BCR_BUSY_READERS, readersToSave)
    return
  }

  const maxDuration = 5 * 60 * 1000
  const busyDuration = date - savedReader.date
  if (maxDuration > busyDuration) {
    return
  }

  if (savedReader.reported) {
    return
  }

  analytics.dispatch(EVENTS.BOOKSY_CARD_READER_ACTION, {
    screen_name: EVENTS_SCREENS.READERS_LIST,
    event_action: EVENTS_ACTION_NAME.READER_TO_LONG_BUSY,
    reader_serial_number: serial_number,
    payment_intent,
  })

  const readersToSave = {
    ...savedReader,
    [String(serial_number)]: {
      ...savedReader,
      reported: true,
    },
  }
  storage.setItem((storage.KEYS as { BCR_BUSY_READERS: string }).BCR_BUSY_READERS, readersToSave)
}

interface UseStripeSdiReturn {
  isSDIMode: ComputedRef<boolean>
  checkReaderBusyTime: (reader: Reader) => void
  processPayment: (readerId: string, paymentIntentId: string) => Promise<unknown>
  cancelPayment: (readerId: string) => Promise<unknown>
  checkIfReaderIsOnline: (readers: Reader[], reader: Reader) => void
  switchIntegrationModeToSDI: () => Promise<void>
}

export function useStripeSdi (): UseStripeSdiReturn {
  const { store } = useCore()
  const storeState = store.state as { business: { stripeAccount: { integration_mode?: string } } }

  return {
    isSDIMode: computed(() => storeState.business.stripeAccount.integration_mode === INTEGRATION_MODES.SDI),
    checkReaderBusyTime,
    processPayment,
    cancelPayment,
    checkIfReaderIsOnline,
    switchIntegrationModeToSDI,
  }
}
